import React, { useEffect, useState } from "react";
import { Drawer, IconButton, Typography, CircularProgress } from "@mui/material";
import { X, File as FileIcon } from "lucide-react";
import { fetchRelatedPCLsByCodeIndexId, RelatedPCLItem } from "../services/pclService";

export interface ReferencedPCLsDrawerProps {
  open: boolean;
  codeIndexId?: string;
  partNumber?: string;
  pcl?: string;
  onClose: () => void;
  onSelectPCL: (pclId: string) => void;
}

export default function ReferencedPCLsDrawer({ open, codeIndexId, partNumber, pcl, onClose, onSelectPCL }: ReferencedPCLsDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<RelatedPCLItem[]>([]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!open || !codeIndexId) return;
      setLoading(true);
      setError(null);
      try {
        const list = await fetchRelatedPCLsByCodeIndexId(codeIndexId);
        if (!ignore) setItems(list);
      } catch (e: any) {
        if (!ignore) setError("Failed to load related PCLs");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [open, codeIndexId]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '360px',
          boxSizing: 'border-box',
        },
      }}
    >
      <div className="h-full flex flex-col bg-white">
        <div className="bg-[#E3F2FD] h-1"></div>
        <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
          <Typography variant="h6" className="font-semibold text-gray-900">Referenced PCLs</Typography>
          <IconButton onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </IconButton>
        </div>

        <div className="px-4 py-3 border-b border-gray-100">
          <div className="text-sm text-gray-600">Part Number</div>
          <div className="text-sm font-semibold text-gray-900">{partNumber || '-'}</div>
          <div className="mt-2 text-sm text-gray-600">PCL</div>
          <div className="text-sm font-semibold text-gray-900">{pcl || '-'}</div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center h-full"><CircularProgress size={20} /></div>
          )}
          {!loading && error && (
            <div className="p-4 text-sm text-red-600">{error}</div>
          )}
          {!loading && !error && (
            <div className="px-4 py-3">
              <div className="text-xs font-medium text-gray-600 mb-2">Related PCLs</div>
              <div className="space-y-2">
                {items.map((p) => (
                  <button key={p.id} className="w-full text-left" onClick={() => onSelectPCL(p.id)}>
                    <div className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md px-3 py-2">
                      <div className="w-6 h-6 rounded bg-[#E6F0FF] flex items-center justify-center">
                        <FileIcon className="h-4 w-4 text-[#1D5176]" />
                      </div>
                      <div className="text-sm font-medium text-gray-900">{p.name || p.id}</div>
                    </div>
                  </button>
                ))}
                {!items.length && (
                  <div className="p-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded">No related PCLs</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}


